import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { Icon, type IconName } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { useTenants } from "../data/queries";
import { ShellSlot } from "./shell";

/** 带子导航的页面框架（工作区级的计费 / 市场页共用） */
export interface SubNavItem {
  id: string;
  to: string;
  label: string;
  icon: IconName;
}

export function SubFrame({ label, items, active, children, wide = true }: { label: string; items: SubNavItem[]; active: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className="subframe">
      <nav className="subnav" aria-label={label}>
        {items.map((s) => (
          <NavLink
            key={s.id}
            to={s.to}
            end
            className={`pnav__link${active === s.id ? " is-active" : ""}`}
            aria-current={active === s.id ? "page" : undefined}
            data-testid={`subnav-${s.id}`}
          >
            <Icon name={s.icon} size={16} />
            {s.label}
          </NavLink>
        ))}
      </nav>
      <div className="subframe__body">
        <div className={wide ? "page page--wide" : "page"}>{children}</div>
      </div>
    </div>
  );
}

/** 顶栏标题：工作区名 / 当前区域 */
export function TenantCrumb({ tid, section }: { tid: string; section: string }) {
  const tenants = useTenants();
  const name = tenants.data?.find((x) => x.id === tid)?.name ?? "";
  return (
    <ShellSlot name="title">
      <Link to={`/t/${tid}`} className="muted ellipsis">
        {name}
      </Link>
      <span className="muted" aria-hidden="true">
        /
      </span>
      <strong className="ellipsis">{section}</strong>
    </ShellSlot>
  );
}

/** 工作区快捷入口（计费 / 市场 / 能力与集成）；useT 在调用处 */
export function useTenantLinks(tid: string) {
  const { t } = useT();
  return [
    { to: `/t/${tid}/billing`, label: t("billing.title"), icon: "wallet" as const, testid: "link-billing" },
    { to: `/t/${tid}/market`, label: t("market.title"), icon: "store" as const, testid: "link-market" },
    { to: `/t/${tid}/capabilities`, label: t("cap.title"), icon: "sparkles" as const, testid: "link-capabilities" },
  ];
}
